import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoFiltersCard from "../../../components/eco-ui/EcoFiltersCard";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import { useGoBack } from "../../../hooks/useGoBack";

import { DreParams, getDreApi, getDreCsvBlobApi } from "../api/financeiroApi";
import type { FinDre, FinDreBasis, FinDreColumn, FinDreRow } from "../api/finTypes";
import CostCenterSelect from "../components/CostCenterSelect";
import { FINANCEIRO_PAGE, FIN_PAYABLES_PAGE } from "../routes";
import { downloadBlob, formatCurrency } from "../../dp/utils/format";
import { periodLabel, useFinPermissions } from "../utils/fin";
import { useCostCenters } from "../utils/costCenters";
import "../../dp/pages/DpEco.css";
import "./Financeiro.css";

const COLLAPSED_KEY = "fin.dre.collapsed";

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const readCollapsed = (): Set<string> => {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(COLLAPSED_KEY) ?? "[]"));
  } catch {
    return new Set();
  }
};

const saveCollapsed = (keys: Set<string>) => {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(keys)));
  } catch {
    /* sem armazenamento: só não lembra */
  }
};

type HeaderCell = { key: string; label: string; colSpan?: number; rowSpan?: number; kind?: FinDreColumn["kind"] };

const columnTitle = (col: FinDreColumn) => (col.kind === "costCenter" ? `${col.code} ${col.label}` : col.label);

/**
 * Cabeçalho em até três linhas: grupo, subgrupo e centro. Coluna sem grupo ("Sem
 * centro de custo", "Total") ocupa as três; coluna direto no grupo ocupa as duas de baixo.
 */
function buildHeader(columns: FinDreColumn[]) {
  const hasSub = columns.some((c) => c.subgroup);
  const depth = hasSub ? 3 : 2;
  const rows: HeaderCell[][] = Array.from({ length: depth }, () => []);
  const groupKey = (c: FinDreColumn) => (c.group ? c.group.id ?? "none" : null);

  for (let i = 0; i < columns.length; ) {
    const col = columns[i];
    const gk = groupKey(col);
    if (gk === null) {
      rows[0].push({ key: col.key, label: columnTitle(col), rowSpan: depth, kind: col.kind });
      i += 1;
      continue;
    }
    let j = i;
    while (j < columns.length && groupKey(columns[j]) === gk) j += 1;
    rows[0].push({ key: `h:${gk}`, label: col.group!.label, colSpan: j - i });
    for (let k = i; k < j; ) {
      const c = columns[k];
      if (!c.subgroup) {
        rows[1].push({ key: c.key, label: columnTitle(c), rowSpan: depth - 1, kind: c.kind });
        k += 1;
        continue;
      }
      let m = k;
      while (m < j && columns[m].subgroup?.id === c.subgroup.id) m += 1;
      rows[1].push({ key: `h:${c.subgroup.id}`, label: c.subgroup.label, colSpan: m - k });
      for (let n = k; n < m; n += 1) rows[2].push({ key: columns[n].key, label: columnTitle(columns[n]), kind: columns[n].kind });
      k = m;
    }
    i = j;
  }
  return { rows, depth };
}

const colClass = (kind?: FinDreColumn["kind"]) =>
  kind === "total" ? "finDre__col--total" : kind === "groupTotal" || kind === "subgroupTotal" ? "finDre__col--sub" : "";

function Cell({ value, kind }: { value: string; kind: FinDreColumn["kind"] }) {
  const n = Number(value);
  return (
    <td className={`finDre__num ${colClass(kind)}`}>
      {n === 0 ? (
        <span className="ecoTable__faint">—</span>
      ) : (
        <span className={n < 0 ? "finMoney finMoney--negative" : "finMoney"}>{formatCurrency(value)}</span>
      )}
    </td>
  );
}

/**
 * DRE por centro de custo (17/10/2026): receitas, despesas e resultado pelo plano
 * de contas, nas colunas os centros de custo sob grupo e subgrupo, com totais.
 * Regime de competência (padrão) ou caixa. Filtros na URL para ter link próprio.
 */
export default function DrePage() {
  const navigate = useNavigate();
  const goBack = useGoBack(FINANCEIRO_PAGE());
  const { token } = useFinPermissions();
  const [params, setParams] = useSearchParams();
  const { costCenters, groups } = useCostCenters(token);

  const now = currentMonth();
  const from = params.get("de") || `${now.slice(0, 4)}-01`;
  const to = params.get("ate") || now;
  const basis: FinDreBasis = params.get("regime") === "caixa" ? "cash" : "competence";
  const groupId = params.get("grupo") ?? "";
  const costCenterId = params.get("centro") ?? "";

  const [data, setData] = useState<FinDre | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed);

  const setParam = (changes: Record<string, string>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
        return next;
      },
      { replace: true }
    );

  const query: DreParams | null = useMemo(
    () =>
      from && to && from <= to
        ? { from, to, basis, groupId, costCenterId: costCenterId === "sem" ? "none" : costCenterId }
        : null,
    [from, to, basis, groupId, costCenterId]
  );

  useEffect(() => {
    if (!token || !query) return;
    let alive = true;
    setLoading(true);
    setError(null);
    getDreApi(token, query)
      .then((dre) => alive && setData(dre))
      .catch((err: Error) => {
        if (!alive) return;
        setError(err.message);
        setData(null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token, query]);

  const header = useMemo(() => (data ? buildHeader(data.columns) : null), [data]);

  // Linhas visíveis: a subcategoria some quando o grupo dela está recolhido.
  const visibleRows = useMemo(() => {
    if (!data) return [];
    const out: FinDreRow[] = [];
    let hidden = false;
    for (const row of data.rows) {
      if (row.kind === "category") hidden = collapsed.has(row.key);
      else if (row.kind !== "subcategory") hidden = false;
      if (row.kind === "subcategory" && hidden) continue;
      out.push(row);
    }
    return out;
  }, [data, collapsed]);

  const toggle = (key: string) => {
    const next = new Set(collapsed);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setCollapsed(next);
    saveCollapsed(next);
  };

  const exportCsv = async () => {
    if (!query) return;
    setExporting(true);
    try {
      const blob = await getDreCsvBlobApi(token, query);
      downloadBlob(blob, `dre-${basis === "cash" ? "caixa" : "competencia"}-${from}-a-${to}.csv`);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao exportar a DRE.");
    } finally {
      setExporting(false);
    }
  };

  const activeCount = (params.get("de") || params.get("ate") ? 1 : 0) + (groupId ? 1 : 0) + (costCenterId ? 1 : 0);
  const groupName = (() => {
    for (const g of groups) {
      if (g.id === groupId) return `${g.code} — ${g.name}`;
      const sub = g.children.find((c) => c.id === groupId);
      if (sub) return `${sub.code} — ${g.name} › ${sub.name}`;
    }
    return null;
  })();
  const summaryParts = [
    `${periodLabel(from)} a ${periodLabel(to)}`,
    basis === "cash" ? "Caixa" : "Competência",
    groupName,
    costCenterId === "sem" ? "Sem centro de custo" : costCenters.find((c) => c.id === costCenterId)?.name,
  ].filter(Boolean);
  const unclassified = data ? Number(data.summary.unclassified) : 0;

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[{ label: "Financeiro", onClick: () => navigate(FINANCEIRO_PAGE()) }, { label: "DRE por centro de custo" }]}
          title="DRE por centro de custo"
          subtitle="Receitas, despesas e resultado pelo plano de contas, consolidados por grupo e subgrupo de centro de custo."
          onBack={goBack}
          backLabel="Voltar ao Financeiro"
          actions={
            <button type="button" className="ecoBtn" onClick={exportCsv} disabled={!data || exporting}>
              {exporting ? "Exportando..." : "Exportar CSV"}
            </button>
          }
        />

        <div className="finToolbar">
          <EcoSegmented<FinDreBasis>
            label="Regime"
            options={[
              { value: "competence", label: "Competência" },
              { value: "cash", label: "Caixa" },
            ]}
            value={basis}
            onChange={(next) => setParam({ regime: next === "cash" ? "caixa" : "" })}
          />
        </div>

        <EcoFiltersCard summary={summaryParts.join(" · ")} activeCount={activeCount}>
          <div className="ecoFilters__grid">
            <div className="ecoField">
              <label htmlFor="dre-de">De</label>
              <input
                id="dre-de"
                type="month"
                className="ecoInput ecoInput--sm"
                value={from}
                max={to}
                onChange={(e) => setParam({ de: e.target.value })}
              />
            </div>
            <div className="ecoField">
              <label htmlFor="dre-ate">Até</label>
              <input
                id="dre-ate"
                type="month"
                className="ecoInput ecoInput--sm"
                value={to}
                min={from}
                onChange={(e) => setParam({ ate: e.target.value })}
              />
            </div>
            {groups.length > 0 ? (
              <div className="ecoField">
                <label htmlFor="dre-grp">Grupo</label>
                <select
                  id="dre-grp"
                  className="ecoSelect ecoSelect--sm"
                  value={groupId}
                  onChange={(e) => setParam({ grupo: e.target.value })}
                >
                  <option value="">Todos</option>
                  {groups.map((g) => (
                    <React.Fragment key={g.id}>
                      <option value={g.id}>
                        {g.code} — {g.name}
                      </option>
                      {g.children.map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.code} — {g.name} › {sub.name}
                        </option>
                      ))}
                    </React.Fragment>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="ecoField">
              <label htmlFor="dre-cc">Centro de custo</label>
              <CostCenterSelect
                id="dre-cc"
                className="ecoSelect ecoSelect--sm"
                value={costCenterId}
                onChange={(v) => setParam({ centro: v })}
                costCenters={costCenters}
                groups={groups}
                placeholder="Todos"
                extraOptions={[{ value: "sem", label: "Sem centro de custo" }]}
              />
            </div>
          </div>
        </EcoFiltersCard>

        {!query ? <p className="dpNotice dpNotice--warning">O mês final é anterior ao inicial.</p> : null}
        {error ? <p className="dpNotice dpNotice--warning">{error}</p> : null}

        {data ? (
          <div className={loading ? "finDash--loading" : ""}>
            <div className="finSummary">
              <div className="finStat">
                <span className="finStat__label">Receitas</span>
                <span className="finStat__value finMoney finMoney--in">{formatCurrency(data.summary.revenue)}</span>
              </div>
              <div className="finStat">
                <span className="finStat__label">Despesas</span>
                <span className="finStat__value finMoney finMoney--out">{formatCurrency(data.summary.expense)}</span>
              </div>
              <div className="finStat">
                <span className="finStat__label">Resultado</span>
                <span className={`finStat__value finMoney ${Number(data.summary.result) < 0 ? "finMoney--negative" : ""}`}>
                  {formatCurrency(data.summary.result)}
                </span>
                <span className="finStat__hint">
                  {data.count} {basis === "cash" ? "baixa(s)" : "lançamento(s)"} no período
                </span>
              </div>
            </div>

            {unclassified !== 0 ? (
              <p className="dpNotice dpNotice--warning finDre__notice">
                {formatCurrency(Math.abs(unclassified))} do período estão sem centro de custo.{" "}
                <Link to={`${FIN_PAYABLES_PAGE()}?centro=sem&periodo=todos&situacao=todos`}>Classificar</Link>
              </p>
            ) : null}

            {data.rows.every((r) => r.kind !== "category") ? (
              <p className="ecoEmpty">Nenhum lançamento no período.</p>
            ) : (
              <section className="ecoCard finDre__card">
                <div className="finDre__wrap">
                  <table className="finDre">
                    <thead>
                      {header!.rows.map((cells, r) => (
                        <tr key={r}>
                          {r === 0 ? (
                            <th rowSpan={header!.depth} className="finDre__label finDre__corner">
                              Conta
                            </th>
                          ) : null}
                          {cells.map((cell) => (
                            <th
                              key={cell.key}
                              colSpan={cell.colSpan}
                              rowSpan={cell.rowSpan}
                              className={`${cell.colSpan ? "finDre__groupHead" : "finDre__num"} ${colClass(cell.kind)}`}
                              scope={cell.colSpan ? "colgroup" : "col"}
                            >
                              {cell.label}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {visibleRows.map((row) => (
                        <tr key={row.key} className={`finDre__row finDre__row--${row.kind}`}>
                          <th scope="row" className="finDre__label">
                            {row.kind === "category" ? (
                              <button
                                type="button"
                                className="finDre__toggle"
                                onClick={() => toggle(row.key)}
                                aria-expanded={!collapsed.has(row.key)}
                              >
                                <span aria-hidden="true">{collapsed.has(row.key) ? "▸" : "▾"}</span>
                                {row.code ? <span className="finTree__code">{row.code}</span> : null}
                                {row.label}
                              </button>
                            ) : (
                              <span className={row.kind === "subcategory" ? "finDre__sublabel" : ""}>
                                {row.kind === "subcategory" && row.code ? <span className="finTree__code">{row.code}</span> : null}
                                {row.label}
                              </span>
                            )}
                          </th>
                          {data.columns.map((col) => (
                            <Cell key={col.key} value={row.cells[col.key]} kind={col.kind} />
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        ) : loading ? (
          <p className="ecoEmpty">Carregando...</p>
        ) : null}
      </main>
    </div>
  );
}
